import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import type { PlanningUnitFormInterface } from '@zaoblako/shared/common/dtos/planning-unit/planning-unit-form.dto';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import type { EmailFindInterface } from '@zaoblako/shared/modules/integration/dtos/email/email-find.dto';
import type { MainSmsAccountFindInterface } from '@zaoblako/shared/modules/integration/dtos/main-sms/main-sms-account-find.dto';
import type { MailingTemplateFormInterface } from '@zaoblako/shared/modules/notification/dtos/mailing/mailing-template-form.dto';
import type { MailingTemplateFindInterface } from '@zaoblako/shared/modules/notification/dtos/mailing/mailing-template-find.dto';
import type { ExternalUserFindInterface } from '@zaoblako/shared/modules/security/dtos/external-user/external-user-find.dto';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { DirectionService } from '@zaoblako/web/core/services/finance/direction.service';
import { IntegrationEmailService } from '@zaoblako/web/core/services/integration/integration-email.service';
import { IntegrationMainsmsService } from '@zaoblako/web/core/services/integration/integration-mainsms.service';
import { MailingHelpdeskService } from '@zaoblako/web/core/services/notification/mailing-helpdesk.service';
import { MailingTemplateService } from '@zaoblako/web/core/services/report/mailing-template.service';
import { NewsletterTemplateFormComponent } from '@zaoblako/web/modules/debts/dumbs/newsletter-template-form/newsletter-template-form.component';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { forkJoin, Observable } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-newsletter-template-form-sidebar',
  templateUrl: './newsletter-template-form-sidebar.component.html',
  imports: [CommonModule, NewsletterTemplateFormComponent, SpinnerComponent],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NewsletterTemplateFormSidebarComponent implements OnInit {
  @Input() public readonly index: number;

  public template = <MailingTemplateFormInterface>{};
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public emails: EmailFindInterface[] = [];
  public arraySms: MainSmsAccountFindInterface[] = [];
  public externalUsers: ExternalUserFindInterface[] = [];
  public recipientUsers: UserFindInterface[] = [];
  public directions: DirectionFindInterface[] = [];

  public constructor(
    private readonly formStoreService: FormStoreService,
    private readonly mailingTemplateService: MailingTemplateService,
    private readonly mailingHelpdeskService: MailingHelpdeskService,
    private readonly integrationMainsmsService: IntegrationMainsmsService,
    private readonly integrationEmailService: IntegrationEmailService,
    private readonly directionService: DirectionService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly errorService: ErrorService,
    private readonly confirmService: ConfirmDialogService
  ) {}

  public ngOnInit(): void {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    forkJoin([
      this.integrationEmailService.find<EmailFindInterface>(),
      this.integrationMainsmsService.find<MainSmsAccountFindInterface>(),
      this.directionService.find<DirectionFindInterface>()
    ]).subscribe(([emails, arraySms, directions]) => {
      this.emails = emails;
      this.arraySms = arraySms;
      this.directions = directions;
      this.cdRef.markForCheck();
    });

    forkJoin([
      this.mailingHelpdeskService.getHelpdeskContacts(),
      this.mailingHelpdeskService.getHelpdeskUsers()
    ]).subscribe(([contacts, recipientUsers]) => {
      this.externalUsers = contacts;
      this.recipientUsers = recipientUsers;
      this.cdRef.markForCheck();
    });

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    } else {
      this.template.includingAdvancePayment = false;
      this.template.planning = <PlanningUnitFormInterface>{};
    }
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.mailingTemplateService
      .findById<MailingTemplateFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (template) => {
          this.template = template;
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveNewsletterTemplate(template: MailingTemplateFormInterface) {
    if (this.formStoreService.getState.forms[this.index]?.entityId) {
      this.formStoreService.setLoading(true, this.index);

      this.mailingTemplateService
        .update<MailingTemplateFormInterface, MailingTemplateFindInterface>(template)
        .subscribe({
          next: (template) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage(`Шаблон рассылки "${template?.name}" обновлен!`);
            this.formStoreService.saveNext(template, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
    } else {
      this.formStoreService.setLoading(true, this.index);

      this.mailingTemplateService.create<MailingTemplateFindInterface>(template).subscribe({
        next: (template) => {
          this.formStoreService.setLoading(false, this.index);
          this.errorService.addSuccessMessage('Шаблон рассылки создан!');
          this.formStoreService.saveNext(template, this.index);
        },
        error: () => {
          this.formStoreService.setLoading(false, this.index);
        }
      });
    }
  }

  public deleteNewsletterTemplate() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить шаблон рассылки "${this.template.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.mailingTemplateService
          .deleteById(this.formStoreService.getState.forms[this.index]?.entityId)
          .subscribe(() => {
            this.errorService.addSuccessMessage(`Шаблон рассылки "${this.template.name}" удален!`);
            this.formStoreService.deleteNext(this.index);
          });
      }
    });
  }
}
